
import React, { useState } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ColorProvider, ThemeProvider, BrandProvider, useColor, useTheme, useBrand } from './context/ColorContext';
import { Toaster } from 'sonner';
import Layout from './components/common/Layout';
import Home from './pages/Home';
import Kartelalar from './pages/Kartelalar';
import KartelaDetail from './pages/KartelaDetail';
import Urunler from './pages/Urunler';
import UrunDetail from './pages/UrunDetail';
import Ilham from './pages/Ilham';
import ArticleDetail from './pages/ArticleDetail';
import Bayiler from './pages/Bayiler';
import Hakkimizda from './pages/Hakkimizda';
import Uretim from './pages/Uretim';
import Iletisim from './pages/Iletisim';
import KVKK from './pages/KVKK';
import CerezPolitikasi from './pages/CerezPolitikasi';

// Admin Pages - Code Splitting for Performance
const AdminLayout = React.lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const AdminCategories = React.lazy(() => import('./pages/admin/Categories'));
const AdminProducts = React.lazy(() => import('./pages/admin/Products'));
const AdminProductEdit = React.lazy(() => import('./pages/admin/ProductEdit'));
const AdminKartelas = React.lazy(() => import('./pages/admin/Kartelas'));
const AdminDealers = React.lazy(() => import('./pages/admin/Dealers'));
const AdminHomeVisualizer = React.lazy(() => import('./pages/admin/HomeVisualizerAdmin'));
const AdminArticles = React.lazy(() => import('./pages/admin/Articles'));
const AdminArticleEdit = React.lazy(() => import('./pages/admin/ArticleEdit'));
const AdminSettings = React.lazy(() => import('./pages/admin/Settings'));
const AdminPages = React.lazy(() => import('./pages/admin/PagesAdmin'));

import VisualizerModal from './components/modals/VisualizerModal';
import SearchModal from './components/modals/SearchModal';
import LightboxModal from './components/modals/LightboxModal';
import { INSPIRATIONS } from './data/constants';

const MainLayout = () => {
    const { activeColor, isDbLoading, settings } = useColor();
    const { theme } = useTheme();
    const { activeBrand } = useBrand();
    const [isVisualizerOpen, setIsVisualizerOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [lightbox, setLightbox] = useState({ isOpen: false, index: 0, images: INSPIRATIONS });

    const [isAppReady, setIsAppReady] = useState(false);
    const [removeLoader, setRemoveLoader] = useState(false);

    React.useEffect(() => {
        // Veritabanı yüklendiğinde cross-fade efektini (fade out loader / fade in site) başlatır
        if (!isDbLoading) {
            setIsAppReady(true);
            const timer = setTimeout(() => {
                setRemoveLoader(true); // Gecikmeyi düşürdük, anında geçiş sağlansın (300ms sadece animasyon için)
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isDbLoading]);

    const openLightbox = React.useCallback((index, images = INSPIRATIONS) => {
        setLightbox({ isOpen: true, index, images });
    }, []);

    const toggleVisualizer = React.useCallback((val) => {
        setIsVisualizerOpen(val);
    }, []);

    const toggleSearch = React.useCallback((val) => {
        setIsSearchOpen(val);
    }, []);

    // Background style based on active color, brand, and theme
    const activeColorCode = activeColor?.code || activeBrand.primaryColor;
    const darkBg = (activeBrand.theme?.dark || '').replace('#5AA668', activeColorCode);
    const lightBg = (activeBrand.theme?.light || '').replace('#5AA668', activeColorCode);

    const location = useLocation();
    const isAdmin = location.pathname.startsWith('/admin');
    const isHome = location.pathname === '/';

    // Sayfa (route) değişince scroll'u her zaman en tepeden başlat
    React.useEffect(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    }, [location.pathname]);

    React.useEffect(() => {
        // ÇÖZÜNÜRLÜK BAĞIMSIZ ÖLÇEKLEME
        // 1080p (1920px genişlik) tasarımını sabit bir "tuval" gibi ele alıp ekran
        // genişliğine ölçekler (zoom = innerWidth / 1920). Böylece görünüm HER
        // çözünürlükte 1080p ile birebir aynı kalır; yüksek DPI (Retina) ekranlarda
        // içerik küçülüp ortada boşluk oluşmaz. Mobil/tablet ve /admin hariç tutulur.
        const DESIGN_WIDTH = 1920;
        const root = document.getElementById('root');

        const applyDesignScale = () => {
            try {
                // Eski/hatalı zoom düzeltmelerini temizle
                document.body.style.zoom = '';
                document.documentElement.style.zoom = '';

                const hasMatchMedia = typeof window !== 'undefined' && !!window.matchMedia;
                const isTouch = 'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0;
                const isFinePointer = hasMatchMedia ? window.matchMedia('(pointer: fine)').matches : true;
                // Sadece masaüstü (fine pointer, dokunmatik değil) ve >=1024px ekranlarda,
                // admin paneli dışında ölçekle. Diğer durumlarda doğal responsive düzen.
                const enableScale = isFinePointer && !isTouch && window.innerWidth >= 1024 && !isAdmin;

                if (root && enableScale) {
                    const z = window.innerWidth / DESIGN_WIDTH;
                    root.style.width = DESIGN_WIDTH + 'px';
                    root.style.zoom = String(z);
                    document.documentElement.style.setProperty('--app-zoom', String(z));
                } else if (root) {
                    root.style.width = '';
                    root.style.zoom = '';
                    document.documentElement.style.setProperty('--app-zoom', '1');
                }
            } catch (error) {
                console.warn('Design scale skipped:', error);
                if (root) { root.style.width = ''; root.style.zoom = ''; }
                document.documentElement.style.setProperty('--app-zoom', '1');
            }
        };

        applyDesignScale();
        window.addEventListener('resize', applyDesignScale);
        return () => window.removeEventListener('resize', applyDesignScale);
    }, [isAdmin]);

    const backgroundStyle = React.useMemo(() => {
        if (isAdmin) return {};
        return {
            backgroundImage: theme === 'dark' ? darkBg : lightBg,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transition: 'background 1s ease-in-out'
        };
    }, [isAdmin, theme, darkBg, lightBg]);

    // Admin panelden yonetilen sabit arkaplan gorseli
    const bgEnabled = settings?.BACKGROUND_ENABLED === 'true';
    const bgHasImage = settings?.BACKGROUND_HAS_IMAGE === 'true';
    const bgAllPages = settings?.BACKGROUND_ALL_PAGES === 'true';
    const bgBlur = Math.max(0, Math.min(50, parseInt(settings?.BACKGROUND_BLUR ?? '0', 10) || 0));
    const bgVersion = settings?.BACKGROUND_IMAGE_VERSION || '0';
    const showBackgroundImage = !isAdmin && bgEnabled && bgHasImage && (bgAllPages || isHome);

    const backgroundImageStyle = React.useMemo(() => ({
        // blur'lu kenarlarda transparan kalmasin diye hafif scale
        filter: bgBlur > 0 ? `blur(${bgBlur}px)` : undefined,
        transform: bgBlur > 0 ? 'scale(1.08)' : undefined,
        transition: 'filter 0.3s ease',
    }), [bgBlur]);

    // Shared page props
    const pageProps = {
        openLightbox,
        onOpenVisualizer: () => setIsVisualizerOpen(true)
    };

    return (
        <>
            {/* Yükleme (Loader) Katmanı - Fade Out Efektiyle Kaybolur */}
            {!removeLoader && (
                <div
                    className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${theme === 'dark' ? 'bg-slate-900' : 'bg-slate-50'} font-sans transition-opacity duration-[1200ms] ease-in-out ${isAppReady ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                >
                    <div className="animate-slow-reveal flex flex-col items-center gap-6">
                        <img
                            src={activeBrand.logo}
                            alt={activeBrand.name}
                            width={140}
                            height={140}
                            fetchPriority="high"
                            className={`h-20 md:h-28 object-contain drop-shadow-2xl grayscale-[20%] transition-opacity duration-1000 ${isAppReady ? 'opacity-0' : 'opacity-100'}`}
                        />
                    </div>
                </div>
            )}

            {/* GLOBAL FIXED BACKGROUND LAYERS - sibling'a tasindi ki hicbir parent transformundan etkilenmesin */}
            {isAppReady && !isAdmin && (
                <div
                    className="fixed inset-0 -z-10 pointer-events-none"
                    style={backgroundStyle}
                    aria-hidden="true"
                />
            )}
            {isAppReady && showBackgroundImage && (
                <img
                    src={`/api/images/background?v=${bgVersion}`}
                    alt=""
                    aria-hidden="true"
                    className="fixed inset-0 -z-10 pointer-events-none w-full h-full object-cover"
                    style={backgroundImageStyle}
                />
            )}

            {/* Ana Site Katmanı - Fade In Efektiyle Belirir */}
            {isAppReady && (
                <div className={`min-h-screen ${theme === 'dark' ? 'text-white' : 'text-neutral-800'} font-sans selection:bg-[#5AA668]/30 overflow-x-hidden relative animate-fadeIn`} style={{ animationDuration: '1.2s' }}>
                    <Layout
                        onOpenVisualizer={() => toggleVisualizer(true)}
                        onOpenSearch={() => toggleSearch(true)}
                        isVisualizerOpen={isVisualizerOpen}
                        isSearchOpen={isSearchOpen}
                    >
                        <Routes>
                            {/* Ana Rotalar */}
                            <Route path="/">
                                <Route index element={<Home {...pageProps} />} />
                                <Route path="urunler" element={<Urunler />} />
                                <Route path="urunler/:slug" element={<UrunDetail {...pageProps} />} />
                                <Route path="kartelalar" element={<Kartelalar />} />
                                <Route path="kartelalar/:slug" element={<KartelaDetail {...pageProps} />} />
                                <Route path="ilham" element={<Ilham />} />
                                <Route path="ilham/:slug" element={<ArticleDetail />} />
                                <Route path="bayiler" element={<Bayiler />} />
                                <Route path="hakkimizda" element={<Hakkimizda />} />
                                <Route path="uretim" element={<Uretim />} />
                                <Route path="iletisim" element={<Iletisim />} />
                                <Route path="kvkk" element={<KVKK />} />
                                <Route path="cerez-politikasi" element={<CerezPolitikasi />} />
                            </Route>

                            {/* Admin Routes */}
                            <Route path="/admin" element={
                                <React.Suspense fallback={
                                    <div className="h-screen w-full flex items-center justify-center bg-slate-50">
                                        <div className="w-10 h-10 rounded-full border-4 border-[#5AA668] border-t-transparent animate-spin"></div>
                                    </div>
                                }>
                                    <AdminLayout />
                                </React.Suspense>
                            }>
                                <Route index element={<AdminDashboard />} />
                                <Route path="kategoriler" element={<AdminCategories />} />
                                <Route path="urunler" element={<AdminProducts />} />
                                <Route path="urunler/edit/:id" element={<AdminProductEdit />} />
                                <Route path="kartelalar" element={<AdminKartelas />} />
                                <Route path="bayiler" element={<AdminDealers />} />
                                <Route path="ilham" element={<AdminArticles />} />
                                <Route path="ilham/edit/:id" element={<AdminArticleEdit />} />
                                <Route path="anasayfa-kartela" element={<AdminHomeVisualizer />} />
                                <Route path="sayfalar" element={<AdminPages />} />
                                <Route path="ayarlar" element={<AdminSettings />} />
                            </Route>
                        </Routes>

                        <VisualizerModal
                            isOpen={isVisualizerOpen}
                            onClose={() => toggleVisualizer(false)}
                        />

                        <SearchModal
                            isOpen={isSearchOpen}
                            onClose={() => toggleSearch(false)}
                        />

                        <LightboxModal
                            isOpen={lightbox.isOpen}
                            images={lightbox.images}
                            activeIndex={lightbox.index}
                            onClose={() => setLightbox(prev => ({ ...prev, isOpen: false }))}
                            onPrev={() => setLightbox(prev => ({ ...prev, index: (prev.index - 1 + prev.images.length) % prev.images.length }))}
                            onNext={() => setLightbox(prev => ({ ...prev, index: (prev.index + 1) % prev.images.length }))}
                        />
                    </Layout>
                </div>
            )}
        </>
    );
};

import { QueryProvider } from './context/QueryContext';

export default function App() {
    return (
        <HelmetProvider>
            <QueryProvider>
                <Toaster position="top-center" expand={false} richColors />
                <BrandProvider>
                    <ThemeProvider>
                        <ColorProvider>
                            <MainLayout />
                        </ColorProvider>
                    </ThemeProvider>
                </BrandProvider>
            </QueryProvider>
        </HelmetProvider>
    );
}
